import { Controller, type Control, type FieldErrors, type UseFieldArrayReturn, type UseFormRegister, type UseFormSetValue, type UseFormWatch } from "react-hook-form";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { InputText } from "primereact/inputtext";
import { AdminCalendar, FormField } from "@/components/forms";
import { formStyles } from "@/components/forms/formStyles";
import { ensureEndNotBeforeStart, calendarValueToDateOrNull } from "@/lib/date-utils";
import { getRhfErrorMessageAtPath } from "@/lib/rhf-errors";
import type { BookableEventFormData } from "@/lib/event-schemas";

function formatLabel(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() !== "" ? value : fallback;
}

export function SessionFields(props: {
  fields: Array<{ id: string }>;
  errors: FieldErrors<BookableEventFormData>;
  control: Control<BookableEventFormData>;
  watch: UseFormWatch<BookableEventFormData>;
  setValue: UseFormSetValue<BookableEventFormData>;
  register: UseFormRegister<BookableEventFormData>;
  append: UseFieldArrayReturn<BookableEventFormData, "sessions">["append"];
  remove: UseFieldArrayReturn<BookableEventFormData, "sessions">["remove"];
}) {
  const sessionsRootError = getRhfErrorMessageAtPath(props.errors, "sessions");

  return (
    <Panel header="Sessions">
      <div className="flex flex-column gap-2">
        {sessionsRootError ? (
          <small
            className="p-error"
            style={{
              color: "#dc3545",
              display: "block",
              marginBottom: "0.5rem",
            }}
          >
            {sessionsRootError}
          </small>
        ) : null}

        {props.fields.map((field, index) => {
          const startsAtName = `sessions.${index}.startsAt` as const;
          const endsAtName = `sessions.${index}.endsAt` as const;
          const labelName = `sessions.${index}.label` as const;

          const startsAtError = getRhfErrorMessageAtPath(props.errors, startsAtName);
          const endsAtError = getRhfErrorMessageAtPath(props.errors, endsAtName);
          const labelError = getRhfErrorMessageAtPath(props.errors, labelName);

          return (
            <div
              key={field.id}
              className="flex flex-column gap-1"
              style={{
                padding: "1rem",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <div className="flex justify-content-between align-items-center mb-2">
                <label className="font-semibold" style={{ color: "#212529" }}>
                  {formatLabel(props.watch(labelName), `Session ${index + 1}`)}
                </label>
                <Button
                  type="button"
                  icon="pi pi-trash"
                  className="p-button-text p-button-danger p-button-sm"
                  onClick={() => props.remove(index)}
                  aria-label="Remove session"
                />
              </div>

              <div className="grid" style={{ rowGap: "0.25rem" }}>
                <div className="col-12 md:col-6 field">
                  <FormField
                    id={startsAtName}
                    label="Start Date & Time"
                    required
                    error={startsAtError}
                  >
                    <Controller
                      control={props.control}
                      name={startsAtName}
                      defaultValue={new Date()}
                      render={({ field }) => {
                        const currentEnd = props.watch(endsAtName);
                        return (
                          <AdminCalendar
                            value={field.value ?? new Date()}
                            onChange={(e) => {
                              const nextStart = calendarValueToDateOrNull(e.value);
                              if (nextStart === null) return;
                              
                              field.onChange(nextStart);

                              const fixedEnd = ensureEndNotBeforeStart(
                                nextStart,
                                currentEnd,
                              );
                              if (
                                fixedEnd &&
                                currentEnd &&
                                fixedEnd.getTime() !== currentEnd.getTime()
                              ) {
                                props.setValue(endsAtName, fixedEnd, {
                                  shouldValidate: true,
                                });
                              }
                            }}
                            showTime
                            stepMinute={15}
                            timeOnly={false}
                            className={startsAtError ? "p-invalid w-full" : "w-full"}
                            inputClassName={startsAtError ? "p-invalid w-full" : "w-full"}
                            inputStyle={formStyles.input}
                          />
                        );
                      }}
                    />
                  </FormField>
                </div>

                <div className="col-12 md:col-6 field">
                  <FormField
                    id={endsAtName}
                    label="End Date & Time"
                    required
                    error={endsAtError}
                  >
                    <Controller
                      control={props.control}
                      name={endsAtName}
                      defaultValue={new Date()}
                      render={({ field }) => (
                        <AdminCalendar
                          value={field.value ?? new Date()}
                          onChange={(e) => {
                            const dateValue = calendarValueToDateOrNull(e.value);
                            if (dateValue !== null) {
                              field.onChange(dateValue);
                            }
                          }}
                          showTime
                          stepMinute={15}
                          timeOnly={false}
                          className={endsAtError ? "p-invalid w-full" : "w-full"}
                          inputClassName={endsAtError ? "p-invalid w-full" : "w-full"}
                          inputStyle={formStyles.input}
                        />
                      )}
                    />
                  </FormField>
                </div>

                <div className="col-12 md:col-6 field">
                  <FormField
                    id={labelName}
                    label="Label"
                    optional
                    error={labelError}
                  >
                    <InputText
                      {...props.register(labelName)}
                      placeholder="e.g., Morning Session"
                      className="w-full"
                      style={formStyles.input}
                    />
                  </FormField>
                </div>
              </div>
            </div>
          );
        })}

        <Button
          type="button"
          label="Add Session"
          icon="pi pi-plus"
          className="p-button-outlined"
          onClick={() => {
            const now = new Date();
            const endDate = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour later
            props.append({
              startsAt: now,
              endsAt: endDate,
              label: "",
            }, { shouldFocus: false });
          }}
        />
      </div>
    </Panel>
  );
}


